import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from './nav'
import { BarChart3, ClipboardCheck, DollarSign, Gauge, HelpCircle, Layers, LogIn, LogOut, Settings, Shield, ShieldOff, Sparkles } from 'lucide-react'
import Spinner from './Spinner'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from '../store'
import { fetchCurrentUser, devMockSignIn, devMockSignOut, setAdminMode } from '../slices/authSlice'
import { useIsAdmin } from '../hooks/useAdminMode'
import { isDevMock } from '../dev/isDevMock'
import { DiscordIcon } from './DiscordIcon'
import { DISCORD_URL } from '../lib/contact'
import { trackSegmentEvent } from '../lib/segment'
import { sizedAvatar } from '../lib/sizedAvatar'
import type { SettingsTab } from './settingsTabs'
import ThemeToggle from './ThemeToggle'
import './UserMenu.css'

type AuthLoginOptions = {
  screenHint?: 'signup'
  /** Force Auth0 to show the login form even when a session exists. */
  prompt?: 'login'
  /** Clear any Auth0 session before login (needed after api.access.deny). */
  freshSession?: boolean
}

// Login always returns the user to the page they signed in from, `/` included — the new-project
// wizard there is where we want everyone to land, rather than the project list.
export function postLoginReturnTo(pathname: string, search = ''): string {
  return pathname + search
}

export function login(
  dispatch: AppDispatch,
  returnTo?: string,
  screenHintOrOptions?: 'signup' | AuthLoginOptions,
) {
  if (isDevMock()) {
    dispatch(devMockSignIn())
    return
  }
  const options: AuthLoginOptions =
    screenHintOrOptions === 'signup' ? { screenHint: 'signup' } : screenHintOrOptions ?? {}
  const params = new URLSearchParams()
  if (returnTo) params.set('returnTo', returnTo)
  // Open Auth0's Universal Login on the sign-up tab (used by the invite page's "Create an account"
  // CTA). Omitted for a normal sign-in, which lands on the login screen.
  if (options.screenHint) params.set('screen_hint', options.screenHint)
  if (options.prompt) params.set('prompt', options.prompt)
  const loginPath = `/api/auth-login?${params}`
  if (options.freshSession) {
    // A login denied before verification leaves an Auth0 session behind, so clear it first. Auth0
    // matches Allowed Logout URLs on the path only, so `signIn` rides along in the query and tells
    // /verify-email to resume sign-in on its very first render rather than flashing its card.
    const verifyParams = new URLSearchParams({ signIn: '1' })
    if (returnTo) verifyParams.set('returnTo', returnTo)
    window.location.href = `/api/auth-logout?returnTo=${encodeURIComponent(`/verify-email?${verifyParams}`)}`
    return
  }
  window.location.href = loginPath
}

function logout(dispatch: AppDispatch) {
  if (isDevMock()) {
    dispatch(devMockSignOut())
    return
  }
  window.location.href = `/api/auth-logout?returnTo=/`
}

export default function UserMenu({
  sidebarCollapsed = false,
  inTopBar = false,
  onOpenHelp,
  onOpenSettings,
}: {
  sidebarCollapsed?: boolean
  // In the chat top bar the Discord link and the Admin Features (admin-mode preview) toggle live
  // inside this profile menu rather than as standalone top-bar controls. Elsewhere (the sidebar
  // footer) Discord keeps its own always-visible link and this menu stays free of those items.
  inTopBar?: boolean
  onOpenHelp?: () => void
  onOpenSettings?: (tab?: SettingsTab) => void
}) {
  const dispatch = useDispatch<AppDispatch>()
  const { user, loading, checked } = useSelector((state: RootState) => state.auth)
  const adminMode = useSelector((state: RootState) => state.auth.adminMode)
  // Admin nav links are shown to any real admin regardless of the admin-mode preview toggle: the
  // Admin and Guidance pages are the only way to reach those surfaces (and to flip the toggle back),
  // so they must not be hidden when an admin previews as a normal user (see useIsAdmin).
  const isAdmin = useIsAdmin()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [isMobileSheet, setIsMobileSheet] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!checked) {
      dispatch(fetchCurrentUser())
    }
  }, [dispatch, checked])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const sync = () => setIsMobileSheet(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        containerRef.current?.contains(target) ||
        sheetRef.current?.contains(target)
      ) {
        return
      }
      setDropdownOpen(false)
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  useEffect(() => {
    if (!dropdownOpen || !isMobileSheet) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [dropdownOpen, isMobileSheet])

  if (loading && !checked) {
    return (
      <div
        className={`user-menu user-menu--loading${sidebarCollapsed ? ' user-menu--sidebar-collapsed' : ''}`}
        data-testid="user-menu-loading"
      >
        {/* Mirror of the signed-out menu below, invisible (see .user-menu--loading in UserMenu.css):
            hidden children keep their layout, so the box reserves the exact width and height the real
            menu will occupy and the auth-resolve swap moves nothing. A fixed min-width can't do this —
            the old 74px guess was the signed-in end state's math, while the signed-out menu measures
            ~113px, which shoved the top bar's Help/Share/Settings cluster 39px left on resolve (QA bug
            bug-mu2y4dlr-qy5h). Keep the two renderings width-identical. */}
        <ThemeToggle compact className="user-menu__theme-toggle" />
        <button type="button" className="user-menu__sign-in">
          <LogIn size={16} aria-hidden />
          {!sidebarCollapsed && 'Sign In'}
        </button>
      </div>
    )
  }

  if (!user) {
    return (
      <div className={`user-menu${sidebarCollapsed ? ' user-menu--sidebar-collapsed' : ''}`}>
        <ThemeToggle compact className="user-menu__theme-toggle" />
        <button
          type="button"
          className="user-menu__sign-in"
          data-testid="sign-in-button"
          data-analytics-event="nav-sign-in"
          disabled={signingIn}
          aria-busy={signingIn}
          onClick={() => {
            setSigningIn(true)
            login(dispatch, postLoginReturnTo(window.location.pathname, window.location.search))
          }}
          aria-label={signingIn ? 'Signing in' : 'Sign in'}
          title={signingIn ? 'Signing in' : 'Sign in'}
        >
          {signingIn ? (
            <Spinner size={16} className="user-menu__spinner" />
          ) : (
            <LogIn size={16} aria-hidden />
          )}
          {!sidebarCollapsed && (signingIn ? 'Signing in…' : 'Sign In')}
        </button>
      </div>
    )
  }

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const dropdown = dropdownOpen ? (
    <div
      ref={isMobileSheet ? sheetRef : undefined}
      className={isMobileSheet ? 'user-menu__sheet' : undefined}
    >
      {isMobileSheet && (
        <button
          type="button"
          className="user-menu__backdrop"
          aria-label="Close menu"
          data-analytics-event="nav-user-menu-close"
          onClick={() => setDropdownOpen(false)}
        />
      )}
      <div className="user-menu__dropdown" data-testid="user-menu-dropdown">
        {/* An account with no profile name falls back to its email address, so printing both lines
            unchanged shows the same long address twice. Show the second line only when it says
            something the first doesn't; either way the full value is in the title. */}
        <div className="user-menu__dropdown-info">
          <div className="user-menu__dropdown-name" title={user.name}>{user.name}</div>
          {user.email && user.email !== user.name && (
            <div className="user-menu__dropdown-email" title={user.email}>{user.email}</div>
          )}
        </div>
        <button
          className="user-menu__dropdown-item"
          type="button"
          data-testid="user-menu-settings"
          data-analytics-event="nav-user-menu-settings"
          onClick={() => {
            setDropdownOpen(false)
            onOpenSettings?.('billing')
          }}
        >
          <Settings size={16} aria-hidden />
          Settings
        </button>
        <ThemeToggle className="user-menu__dropdown-item" />
        {/* Release notes are public product news, so this is shown to all signed-in users (unlike
            the admin-only Admin / Guidance items below). */}
        <Link
          className="user-menu__dropdown-item"
          to="/release-notes"
          data-testid="user-menu-release-notes"
          data-analytics-event="nav-user-menu-release-notes"
          onClick={() => setDropdownOpen(false)}
        >
          <Sparkles size={16} aria-hidden />
          Release Notes
        </Link>
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin"
            data-testid="user-menu-admin"
            data-analytics-event="nav-user-menu-admin"
            onClick={() => setDropdownOpen(false)}
          >
            <Shield size={16} aria-hidden />
            Admin
          </Link>
        )}
        {/* Admin Features (the admin-mode preview toggle) used to sit in the chat top bar; it now
            lives here in the profile menu. Real admins only. Toggling it doesn't close the menu, so
            the flipped state is visible immediately. */}
        {isAdmin && (
          <button
            className={`user-menu__dropdown-item${adminMode ? ' user-menu__dropdown-item--active' : ''}`}
            type="button"
            aria-pressed={adminMode}
            data-testid="user-menu-admin-features"
            data-analytics-event="admin-mode-toggle"
            onClick={() => dispatch(setAdminMode(!adminMode))}
          >
            {adminMode ? <Shield size={16} aria-hidden /> : <ShieldOff size={16} aria-hidden />}
            Admin Features
          </button>
        )}
        {/* The eval catalogue (/admin/evals). Sits directly under the Admin Features toggle: like the Admin
            link above it is shown to any real admin regardless of the admin-mode preview, since the page is
            the only way to reach it. */}
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin/evals"
            data-testid="user-menu-evals"
            data-analytics-event="nav-user-menu-evals"
            onClick={() => setDropdownOpen(false)}
          >
            <ClipboardCheck size={16} aria-hidden />
            Evals
          </Link>
        )}
        {/* The critics catalogue (/admin/critics) — the prompts that grade bugs, journeys, explorations,
            projects and user days. Shown to any real admin, like Evals above, since the page is the only way
            to reach it. */}
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin/critics"
            data-testid="user-menu-critics"
            data-analytics-event="nav-user-menu-critics"
            onClick={() => setDropdownOpen(false)}
          >
            <Gauge size={16} aria-hidden />
            Critics
          </Link>
        )}
        {/* Cost attribution (/admin/cost). Like Admin and Evals above, shown to any real admin
            regardless of the admin-mode preview, since this is the only way to reach the page. */}
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin/cost"
            data-testid="user-menu-cost"
            data-analytics-event="nav-user-menu-cost"
            onClick={() => setDropdownOpen(false)}
          >
            <DollarSign size={16} aria-hidden />
            Cost
          </Link>
        )}
        {/* Product Analysis (/admin/analysis) — the data-scientist read of customer usage, segments,
            Pro-plan conversion candidates and free-credit consumption. Shown to any real admin, like the
            other admin catalogue links above, since the page is the only way to reach it. */}
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin/analysis"
            data-testid="user-menu-analysis"
            data-analytics-event="nav-user-menu-analysis"
            onClick={() => setDropdownOpen(false)}
          >
            <BarChart3 size={16} aria-hidden />
            Analysis
          </Link>
        )}
        {/* Aggregates (/admin/aggregates) — cross-project aggregate bug and customer data. Shown to any
            real admin, like the other admin catalogue links above, since the page is the only way to
            reach it. */}
        {isAdmin && (
          <Link
            className="user-menu__dropdown-item"
            to="/admin/aggregates"
            data-testid="user-menu-aggregates"
            data-analytics-event="nav-user-menu-aggregates"
            onClick={() => setDropdownOpen(false)}
          >
            <Layers size={16} aria-hidden />
            Aggregates
          </Link>
        )}
        {/* Help and Discord live in the profile menu everywhere except the desktop chat top bar, which
            keeps its own standalone Help button (so we omit Help there to avoid duplicating it). Discord
            has no standalone control anymore, so it always lives in this menu. On the desktop sidebar
            they used to sit in the footer for signed-in users; they now live here instead (see
            AppShell — the footer keeps them only for signed-out desktop users, who have no menu). */}
        {(isMobileSheet || !inTopBar) && (
          <button
            className="user-menu__dropdown-item"
            type="button"
            data-testid="user-menu-help"
            data-analytics-event="nav-user-menu-help"
            onClick={() => {
              setDropdownOpen(false)
              onOpenHelp?.()
            }}
          >
            <HelpCircle size={16} aria-hidden />
            Help
          </button>
        )}
        <a
          className="user-menu__dropdown-item"
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="user-menu-discord"
          data-analytics-event="nav-user-menu-discord"
          onClick={() => {
            trackSegmentEvent('Clicked Join Discord', { location: 'user-menu' })
            setDropdownOpen(false)
          }}
        >
          <DiscordIcon size={16} />
          Discord
        </a>
        <div className="user-menu__dropdown-divider" />
        <button
          className="user-menu__dropdown-item"
          type="button"
          data-testid="sign-out-button"
          data-analytics-event="nav-sign-out"
          disabled={signingOut}
          aria-busy={signingOut}
          onClick={() => {
            setSigningOut(true)
            logout(dispatch)
          }}
        >
          {signingOut ? (
            <Spinner size={16} className="user-menu__spinner" />
          ) : (
            <LogOut size={16} aria-hidden />
          )}
          {signingOut ? 'Signing out…' : 'Sign Out'}
        </button>
      </div>
    </div>
  ) : null

  return (
    <div
      className={`user-menu${sidebarCollapsed ? ' user-menu--sidebar-collapsed' : ''}${dropdownOpen ? ' user-menu--open' : ''}`}
      data-testid="user-menu"
    >
      <div className="user-menu__container" ref={containerRef}>
        <button
          className="user-menu__trigger"
          data-testid="user-menu-trigger"
          data-analytics-event="nav-user-menu-toggle"
          type="button"
          aria-expanded={dropdownOpen}
          aria-haspopup="menu"
          aria-label={sidebarCollapsed || isMobileSheet ? `Account menu (${user.name})` : undefined}
          title={sidebarCollapsed || isMobileSheet ? user.name : undefined}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          {user.picture ? (
            <img
              src={sizedAvatar(user.picture, 56)}
              srcSet={`${sizedAvatar(user.picture, 28)} 1x, ${sizedAvatar(user.picture, 56)} 2x`}
              alt=""
              width={28}
              height={28}
              className="user-menu__avatar"
            />
          ) : (
            <div className="user-menu__avatar-fallback">{initials}</div>
          )}
          <span className="user-menu__name">{user.name}</span>
        </button>
        {dropdown && !isMobileSheet && dropdown}
        {dropdown && isMobileSheet && createPortal(dropdown, document.body)}
      </div>
    </div>
  )
}
